import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import type { PostPlatform, PublishingAccount } from '../../types/publishing';
import type { PostDestinationId } from '../../lib/publishing/destinations';
import {
  buildCoveragePlan,
  multiPreviewNote,
  type CoverageAccount,
} from '../../lib/publishing/multiAccount';
import {
  effectiveCaption,
  emptyComposerState,
  selectedDestinationIds,
  toPostInput,
  type ComposerState,
} from './composerState';
import { fanOutPost, getAccountPlatforms, listPublishingAccounts, type FanOutResponse } from './api';
import { CaptionBox } from './CaptionBox';
import { CaptionWorkspace, CaptionWorkspaceTrigger } from './CaptionWorkspace';
import { DestinationPicker } from './DestinationPicker';
import { DestinationOptionsSection } from './DestinationOptionsSection';
import { FrozenCaptionNotice } from './FrozenCaptionNotice';
import { PerAccountPreviewList } from './PerAccountPreviewList';
import { hasOwnCaption } from '../../lib/publishing/captionOverride';
import { DESTINATION_ORDER } from '../../lib/publishing/destinations';
import type { ComposerTarget } from './composerState';
import { MediaPicker } from './MediaPicker';
import { PostPreviews } from './previews/PostPreviews';
import { MultiAccountPanel } from './MultiAccountPanel';
import { CoveragePanel } from './CoveragePanel';
import { ConfirmFanOutModal } from './ConfirmFanOutModal';
import { isoToLocalInput, localInputToIso } from './ScheduleSection';

// The destinations whose own caption is in effect (same rule as effectiveCaption),
// with their AUTHORED text — the per-account list resolves them per account.
function ownCaptionsFor(targets: Record<PostDestinationId, ComposerTarget>) {
  return DESTINATION_ORDER.filter((d) => hasOwnCaption(d, targets[d])).map((d) => ({
    destination: d,
    text: targets[d].customContent,
  }));
}

// Publishing — multi-account composer (2026-07-28): compose ONCE, fan out to many
// accounts, the caption resolving per account. Structurally the broadcast compose
// flow for posts: pick accounts → shared content → coverage preview → resilient
// fan-out. Reuses the single-composer sub-components; the shared composition is a
// ComposerState whose kyckAccountId is unused (one is filled per account on the
// server). No queue mode (queues are per-profile), no per-destination times
// (§ same scheduledFor across all).

type MultiMode = 'draft' | 'schedule' | 'now';
const MODES: { value: MultiMode; label: string }[] = [
  { value: 'draft', label: 'Save drafts' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'now', label: 'Publish now' },
];

// All destinations are tickable — the AM composes the UNION they want; the
// coverage preview shows which apply per account.
const ALL_CONNECTED: Record<PostPlatform, boolean> = {
  facebook: true,
  instagram: true,
  googlebusiness: true,
};

function toCoverageAccount(a: PublishingAccount): CoverageAccount {
  return { id: a.id, name: a.name, connected: a.connected, brandName: a.brandName };
}

export function MultiComposerPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: ['publishing-accounts'], queryFn: listPublishingAccounts });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [state, setState] = useState<ComposerState>(() => emptyComposerState());
  const [mode, setMode] = useState<MultiMode>('draft');
  const [confirming, setConfirming] = useState(false);
  // The AI drafting workspace is a full-width region ABOVE the three columns.
  const [workspaceOpen, setWorkspaceOpen] = useState(false);

  const selected = selectedDestinationIds(state);

  const selectedAccounts = useMemo(
    () => (accounts.data ?? []).filter((a) => selectedIds.has(a.id)),
    [accounts.data, selectedIds],
  );

  const coveragePlan = useMemo(
    () => buildCoveragePlan(selectedAccounts.map(toCoverageAccount), selected),
    [selectedAccounts, selected],
  );

  // The live preview resolves against the FIRST selected account (the common path
  // is a single-brand batch; a note states merge fields resolve per account).
  const firstAccountId = selectedAccounts[0]?.id ?? '';
  const firstPlatforms = useQuery({
    queryKey: ['publishing-account-platforms', firstAccountId],
    queryFn: () => getAccountPlatforms(firstAccountId),
    enabled: firstAccountId !== '',
  });

  const fanOut = useMutation({
    mutationFn: () => {
      const shared = toPostInput({ ...state, kyckAccountId: 'multi', publishMode: mode });
      return fanOutPost({
        accountIds: [...selectedIds],
        content: shared.content,
        media: shared.media,
        targets: shared.targets,
        scheduledFor: shared.scheduledFor ?? null,
        publishMode: mode,
      });
    },
    onSuccess: () => {
      // The fanned-out posts are now on the calendar; refresh it.
      qc.invalidateQueries({ queryKey: ['publishing-posts'] });
    },
  });

  if (accounts.isLoading) return <CenteredSpinner />;

  const hasAccounts = selectedIds.size > 0;
  const hasDestinations = selected.length > 0;
  const scheduleReady = mode !== 'schedule' || !!state.scheduledFor;
  const canReview = hasAccounts && hasDestinations && scheduleReady;

  const toggleDestination = (dest: PostDestinationId, enabled: boolean) =>
    setState((s) => ({ ...s, targets: { ...s.targets, [dest]: { ...s.targets[dest], enabled } } }));

  const result: FanOutResponse | null = fanOut.data ?? null;

  return (
    <div>
      <PageHeader
        title="Post to multiple accounts"
        description="Compose once and fan out — the caption resolves to each account's own details."
        backHref="/publishing"
      />

      {/* ⚠️ sharedAcrossAccounts is LOAD-BEARING here, for exactly the same
          reason the token insert mode below is: one caption goes to many
          businesses, so the prompt is told not to name this one. The voice
          grounding uses the FIRST selected account, mirroring how the preview
          resolves. */}
      {workspaceOpen && (
        <CaptionWorkspace
          kyckAccountId={firstAccountId}
          destinations={selected}
          media={state.media}
          currentCaption={state.content}
          sharedAcrossAccounts
          onUse={(content) => setState((s) => ({ ...s, content }))}
          onClose={() => setWorkspaceOpen(false)}
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_minmax(0,1fr)_360px]">
        {/* accounts column */}
        <div>
          <MultiAccountPanel
            accounts={accounts.data ?? []}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
          />
        </div>

        {/* composition column */}
        <div className="space-y-5">
          {!hasAccounts ? (
            <p className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
              Select one or more accounts to compose a post for all of them.
            </p>
          ) : (
            <>
              <DestinationPicker
                selected={selected}
                connected={ALL_CONNECTED}
                onToggle={toggleDestination}
              />

              {/* ⚠️ wildcardMode="token" is LOAD-BEARING here: the fan-out resolves
                  the caption once PER ACCOUNT at dispatch, so inserting one
                  account's resolved text would send it to all of them. Never
                  "resolved" on this page. */}
              <FrozenCaptionNotice targets={state.targets} />
              <CaptionBox
                value={state.content}
                selected={selected}
                effectiveCaptionFor={(dest) => effectiveCaption(state, dest)}
                wildcardMode="token"
                wildcardContext={firstPlatforms.data?.wildcard ?? null}
                wildcardAccountId={firstAccountId || null}
                onChange={(content) => setState((s) => ({ ...s, content }))}
              />
              <CaptionWorkspaceTrigger
                kyckAccountId={firstAccountId}
                destinations={selected}
                open={workspaceOpen}
                onOpen={() => {
                  setWorkspaceOpen(true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Media</label>
                <MediaPicker
                  media={state.media}
                  onChange={(media) => setState((s) => ({ ...s, media }))}
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Uploaded once and reused across every account.
                </p>
              </div>

              {selected.length > 0 && (
                <DestinationOptionsSection
                  targets={state.targets}
                  baseCaption={state.content}
                  onChange={(targets) => setState((s) => ({ ...s, targets }))}
                />
              )}

              {/* dispatch controls — draft / schedule / now (no queue, one shared time) */}
              <div className="space-y-3 rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap gap-1">
                  {MODES.map((m) => (
                    <Button
                      key={m.value}
                      type="button"
                      size="sm"
                      variant={mode === m.value ? 'default' : 'outline'}
                      onClick={() => setMode(m.value)}
                    >
                      {m.label}
                    </Button>
                  ))}
                </div>
                {mode === 'schedule' && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500" htmlFor="multi-schedule">
                      Date &amp; time — all accounts
                    </label>
                    <input
                      id="multi-schedule"
                      type="datetime-local"
                      value={isoToLocalInput(state.scheduledFor)}
                      className="block h-9 w-full rounded-md border border-slate-200 px-2 text-sm"
                      onChange={(e) =>
                        setState((s) => ({ ...s, scheduledFor: localInputToIso(e.target.value) }))
                      }
                    />
                  </div>
                )}
                <Button
                  type="button"
                  className="w-full"
                  disabled={!canReview}
                  onClick={() => setConfirming(true)}
                >
                  Review &amp; post to {selectedIds.size} account{selectedIds.size === 1 ? '' : 's'}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* coverage + preview column */}
        <div className="space-y-5 lg:sticky lg:top-6 self-start">
          <CoveragePanel plan={coveragePlan} />
          {hasAccounts && hasDestinations && (
            <div>
              <p className="mb-1 text-xs text-slate-600">
                {multiPreviewNote(selectedAccounts[0]?.name ?? '', selectedAccounts.length)}
              </p>
              <PostPreviews
                state={state}
                platforms={firstPlatforms.data ?? null}
                issues={[]}
                wildcard={firstPlatforms.data?.wildcard ?? null}
              />
            </div>
          )}
          {hasAccounts && hasDestinations && (
            <PerAccountPreviewList
              coverage={coveragePlan}
              baseCaption={state.content}
              ownCaptions={ownCaptionsFor(state.targets)}
              state={state}
            />
          )}
        </div>
      </div>

      {confirming && (
        <ConfirmFanOutModal
          plan={coveragePlan}
          publishMode={mode}
          busy={fanOut.isPending}
          result={result}
          errorText={fanOut.error instanceof Error ? fanOut.error.message : null}
          onConfirm={() => fanOut.mutate()}
          onClose={() => {
            // After a completed run, "Done" returns to the calendar; otherwise it's
            // a plain cancel.
            const done = !!result;
            setConfirming(false);
            fanOut.reset();
            if (done) {
              toast.success('Multi-account post complete');
              navigate('/publishing');
            }
          }}
        />
      )}
    </div>
  );
}
